import React, { ReactNode } from "react";
import { useUser } from "@/context/UserContext";
import { useAuth0 } from "@auth0/auth0-react";
import { getReferralBonus, getReferredUsers } from "@/services/ReferralService";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface ReferralBonusContextType {
    refetchBonusProperties: () => Promise<any>;
    bonusProperties: { data: any[] };
    refetchReferredUsers: () => Promise<any>;
    referredUsers: { data: any[] };
    resetReferredUsers: () => Promise<void>;
}

export const ReferralBonusContext = React.createContext<ReferralBonusContextType | undefined>(undefined);

interface ReferralBonusProviderProps {
    children: ReactNode;
}

export const ReferralBonusProvider = ({ children }: ReferralBonusProviderProps) => {
    const { userState, isLoading: isUserLoading } = useUser();
    const { isAuthenticated, isLoading: isAuth0Loading } = useAuth0();
    const queryClient = useQueryClient();

    // Function that handles fetching of referred users
    const referredUsersData = useQuery({
        queryKey: ["referredUsersData", userState?.id, isUserLoading, isAuth0Loading],
        queryFn: () => getReferredUsers({ userId: userState?.id }),
        enabled: isAuthenticated && !isUserLoading && !isAuth0Loading && !!userState?.id,
    });

    // Function that handles fetching of referral bonus
    const referralBonusData = useQuery({
        queryKey: ["referralBonusData", userState?.id, isUserLoading, isAuth0Loading],
        queryFn: () => getReferralBonus({ userId: userState?.id }),
        enabled: isAuthenticated && !isUserLoading && !isAuth0Loading && !!userState?.id,
    });

    // Function that resets the referred users
    const resetReferredUsers = async (): Promise<void> => {
        await queryClient.setQueryData(["referredUsersData", userState?.id, isUserLoading, isAuth0Loading], {
            data: { referredUsers: [] },
        });
    };

    return (
        <ReferralBonusContext.Provider
            value={{
                refetchBonusProperties: referralBonusData.refetch,
                bonusProperties: { data: referralBonusData?.data?.data?.bonusProperties ?? [] },
                refetchReferredUsers: referredUsersData.refetch,
                referredUsers: { data: referredUsersData?.data?.data?.referredUsers ?? [] },
                resetReferredUsers,
            }}
        >
            {children}
        </ReferralBonusContext.Provider>
    );
};

export const useReferralBonus = (): ReferralBonusContextType => {
    const context = React.useContext(ReferralBonusContext);
    if (context === undefined) {
        throw new Error("useReferralBonus must be used within a ReferralBonusProvider");
    }
    return context;
};
